/**
 * Group-by-auction support for the lot table.
 *
 * Resellers think in "auctions I'm attending": one pickup trip, one close
 * window, one location. Grouping renders a collapsible header row per auction
 * with decision rollups:
 *   - lot count and active-bid count
 *   - total exposure: out-the-door spend if every active bid wins
 *   - combined meat range across priced lots
 *   - soonest close + location (pickup)
 *
 * Collapsed state lives in module memory (per session) keyed by group key.
 *
 * @module portal/modules/auctionGrouping
 */

import type { Lot, PortalSettings } from '../types';
import { escapeAttr, escapeHtml, formatCurrencyDisplay, parseCurrencyValue } from './formatting';
import { calculateMeatOnBone, resolveLotFees } from './lotOperations';
import { computeSpend } from '@lotlingo/shared';

export interface AuctionGroup {
  key: string;
  title: string;
  lots: Lot[];
}

export interface AuctionGroupRollup {
  lotCount: number;
  biddingCount: number;
  /** Total spend if every active bid wins (bid amounts + fees). */
  totalExposure: number;
  /** Sum of meatLow across lots that have meat math; null when none do. */
  meatLowTotal: number | null;
  /** Sum of meatHigh across lots that have meat math; null when none do. */
  meatHighTotal: number | null;
  /** Soonest effective close timestamp; null when unknown. */
  soonestCloseAt: number | null;
  /** First non-empty lot location (pickup hint). */
  location: string | null;
}

const collapsedGroups = new Set<string>();

export function auctionGroupKey(lot: Lot): string {
  if (lot.auctionId) return `id:${lot.auctionId}`;
  if (lot.auctionTitle) return `title:${lot.auctionTitle}`;
  return `none:${lot.source || 'unknown'}`;
}

function auctionGroupTitle(lot: Lot): string {
  return lot.auctionTitle || lot.auctioneer || 'Other lots';
}

/** Group lots by auction, preserving the incoming (sorted) order. */
export function groupLotsByAuction(lots: Lot[]): AuctionGroup[] {
  const groups: AuctionGroup[] = [];
  const byKey = new Map<string, AuctionGroup>();
  for (const lot of lots) {
    if (!lot) continue;
    const key = auctionGroupKey(lot);
    let group = byKey.get(key);
    if (!group) {
      group = { key, title: auctionGroupTitle(lot), lots: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.lots.push(lot);
  }
  return groups;
}

export function computeGroupRollup(
  lots: Lot[],
  settings: PortalSettings | null | undefined,
  getEffectiveCloseTime: (lot: Lot) => number | null
): AuctionGroupRollup {
  let biddingCount = 0;
  let totalExposure = 0;
  let meatLowTotal: number | null = null;
  let meatHighTotal: number | null = null;
  let soonestCloseAt: number | null = null;
  let location: string | null = null;

  for (const lot of lots) {
    if (!lot) continue;

    if (lot.bidding && typeof lot.bidAmount === 'number' && Number.isFinite(lot.bidAmount)) {
      biddingCount += 1;
      totalExposure += computeSpend(lot.bidAmount, resolveLotFees(lot, settings));
    }

    const meat = calculateMeatOnBone(lot, settings);
    if (meat) {
      if (meat.meatLow != null) meatLowTotal = (meatLowTotal ?? 0) + meat.meatLow;
      if (meat.meatHigh != null) meatHighTotal = (meatHighTotal ?? 0) + meat.meatHigh;
    }

    const closeAt = getEffectiveCloseTime(lot);
    if (closeAt != null && Number.isFinite(closeAt)) {
      if (soonestCloseAt == null || closeAt < soonestCloseAt) soonestCloseAt = closeAt;
    }

    if (!location && typeof lot.location === 'string' && lot.location.trim()) {
      location = lot.location.trim();
    }
  }

  return {
    lotCount: lots.length,
    biddingCount,
    totalExposure: Math.round(totalExposure * 100) / 100,
    meatLowTotal: meatLowTotal != null ? Math.round(meatLowTotal * 100) / 100 : null,
    meatHighTotal: meatHighTotal != null ? Math.round(meatHighTotal * 100) / 100 : null,
    soonestCloseAt,
    location,
  };
}

export function isGroupCollapsed(key: string): boolean {
  return collapsedGroups.has(key);
}

/** Toggle a group's collapsed state; returns the new collapsed value. */
export function toggleGroupCollapsed(key: string): boolean {
  if (collapsedGroups.has(key)) {
    collapsedGroups.delete(key);
    return false;
  }
  collapsedGroups.add(key);
  return true;
}

/** Test hook / logout hygiene. */
export function resetCollapsedGroups(): void {
  collapsedGroups.clear();
}

function formatCloseShort(ts: number | null): string {
  if (ts == null || !Number.isFinite(ts)) return '';
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function money(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return formatCurrencyDisplay(value) ?? '—';
}

/** Build the clickable header row for one auction group. */
export function buildAuctionGroupHeaderRow(
  group: AuctionGroup,
  rollup: AuctionGroupRollup,
  collapsed: boolean,
  colspan: number
): string {
  const chevron = collapsed ? '▸' : '▾';
  const lotsLabel = `${rollup.lotCount} lot${rollup.lotCount === 1 ? '' : 's'}`;

  const stats: string[] = [];
  if (rollup.biddingCount > 0) {
    stats.push(
      `<span class="auction-group__stat" title="Out-the-door spend if all your active bids win">` +
        `Exposure ${money(rollup.totalExposure)} (${rollup.biddingCount} bid${rollup.biddingCount === 1 ? '' : 's'})</span>`
    );
  }
  if (rollup.meatLowTotal != null || rollup.meatHighTotal != null) {
    stats.push(
      `<span class="auction-group__stat" title="Combined profit range across priced lots">` +
        `Meat ${money(rollup.meatLowTotal)} … ${money(rollup.meatHighTotal)}</span>`
    );
  }
  const closeText = formatCloseShort(rollup.soonestCloseAt);
  if (closeText) {
    stats.push(
      `<span class="auction-group__stat" title="Soonest lot close in this auction">Closes ${escapeHtml(closeText)}</span>`
    );
  }
  if (rollup.location) {
    stats.push(
      `<span class="auction-group__stat" title="Pickup location">📍 ${escapeHtml(rollup.location)}</span>`
    );
  }

  return (
    `<tr class="auction-group-row" data-action="toggle-auction-group" data-group-key="${escapeAttr(group.key)}"` +
    ` role="button" tabindex="0" aria-expanded="${collapsed ? 'false' : 'true'}"` +
    ` title="${collapsed ? 'Expand' : 'Collapse'} ${escapeAttr(group.title)}">` +
    `<td colspan="${colspan}">` +
    `<span class="auction-group__chevron" aria-hidden="true">${chevron}</span>` +
    `<span class="auction-group__title">${escapeHtml(group.title)}</span>` +
    `<span class="auction-group__count">${lotsLabel}</span>` +
    `<span class="auction-group__stats">${stats.join('')}</span>` +
    `</td></tr>`
  );
}
